import { useCallback, useEffect } from 'react';
import UIAlert from 'UIComponents/alert/UIAlert';
import { View } from 'foundations-components/components/View';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { BU_PROPERTY_NAME } from 'framework-listing-lib/constants/businessUnits';
import { useHasBUNavIntegration } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import { useCrmObjectType } from 'framework-listing-lib/internal/providers/CrmObjectTypeListingClientProvider';
import useFetchUserSettings from 'framework-listing-lib/internal/userSettings/hooks/useFetchUserSettings';
import useSaveUserSettings from 'framework-listing-lib/internal/userSettings/hooks/useSaveUserSettings';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { HAS_DISMISSED_BANNER_USER_SETTING_KEY, HAS_SEEN_BANNER_USER_SETTING_KEY } from 'framework-listing-lib/internal/businessUnits/constants';
import { BusinessUnitsRebrandMessage } from 'ui-business-units-lib/components/BusinessUnitsRebrandMessage';
import { jsx as _jsx } from "react/jsx-runtime";
function useHasSeenDeprecationBanner() {
  const {
    data
  } = useFetchUserSettings(HAS_SEEN_BANNER_USER_SETTING_KEY, {
    defaultValue: 'false'
  });
  const saveUserSettings = useSaveUserSettings();
  const setter = useCallback(value => {
    saveUserSettings({
      key: HAS_SEEN_BANNER_USER_SETTING_KEY,
      value
    }).catch(() => {});
  }, [saveUserSettings]);
  const value = data === undefined ? data : data.value === 'true';
  return [value, setter];
}
function useHasDismissedDeprecationBanner() {
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const {
    data
  } = useFetchUserSettings(HAS_DISMISSED_BANNER_USER_SETTING_KEY, {
    defaultValue: 'false'
  });
  const saveUserSettings = useSaveUserSettings();
  const setter = useCallback(value => {
    saveUserSettings({
      key: HAS_DISMISSED_BANNER_USER_SETTING_KEY,
      value
    }).catch(() => {});
    if (value === 'true') {
      trackCrmTableInteraction({
        action: 'dismissed-bu-deprecation-banner'
      });
    }
  }, [saveUserSettings, trackCrmTableInteraction]);
  const value = data === undefined ? data : data.value === 'true';
  return [value, setter];
}
export default function BUDeprecationBanner() {
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const hasBUNavIntegration = useHasBUNavIntegration();
  const hasViews = useHasViews();
  const {
    hasPropertyFilter
  } = useCrmObjectType();
  const [hasDismissedDeprecationBanner, setHasDismissedDeprecationBanner] = useHasDismissedDeprecationBanner();
  const [hasSeenDeprecationBanner, setHasSeenDeprecationBanner] = useHasSeenDeprecationBanner();
  const shouldRenderBanner = hasBUNavIntegration && hasPropertyFilter(BU_PROPERTY_NAME) && hasDismissedDeprecationBanner === false;

  /**
   * Tracking has-seen-bu-deprecation-banner only once to prevent us from
   * sending the event for the same user several times.
   */
  useEffect(() => {
    if (shouldRenderBanner && hasSeenDeprecationBanner === false) {
      trackCrmTableInteraction({
        action: 'has-seen-bu-deprecation-banner'
      });
      setHasSeenDeprecationBanner('true');
    }
  }, [hasSeenDeprecationBanner, setHasSeenDeprecationBanner, shouldRenderBanner, trackCrmTableInteraction]);
  if (!shouldRenderBanner) {
    return null;
  }
  return /*#__PURE__*/_jsx(View, Object.assign({
    "data-test-id": "bu-deprecation-banner",
    as: "section"
  }, hasViews && {
    borderInlineWidth: '100',
    borderInlineStyle: 'solid',
    borderInlineColor: 'border-core-default'
  }, {
    paddingBlockStart: "0",
    paddingBlockEnd: "300",
    paddingInline: hasViews ? '300' : '0',
    children: /*#__PURE__*/_jsx(UIAlert, {
      closeable: true,
      onClose: () => setHasDismissedDeprecationBanner('true'),
      titleText: /*#__PURE__*/_jsx(BusinessUnitsRebrandMessage, {
        message: "frameworkListingLib.buSelect.deprecationBanner.title",
        rebrandMessage: "frameworkListingLib.buSelect.deprecationBanner.title_buv2"
      }),
      type: "warning",
      children: /*#__PURE__*/_jsx(BusinessUnitsRebrandMessage, {
        message: "frameworkListingLib.buSelect.deprecationBanner.message",
        rebrandMessage: "frameworkListingLib.buSelect.deprecationBanner.message_buv2"
      })
    })
  }));
}